import { getControlPlaneURL } from "@storytell/network-domains";
import type { AnalyticsPlugin } from "analytics";
import type { CaptureResult, PostHog, Properties } from "posthog-js";

// Coming from vite config in apps/bluesky/vite.config.ts
declare const POSTHOG_API_KEY: string;
declare const RUNNING_ENV: string;
declare const STORYTELL_API_URL: string;

// Tracking calls sit inline with the actions they report, often before them, so
// a throw from posthog-js would abort the user's action. Analytics never does.
const safely = (send: () => void): void => {
  try {
    send();
  } catch {
    /* best-effort */
  }
};

// Checked in before_send, not at our captureException call: exception autocapture is switched on
// from PostHog's project settings and reports window errors and unhandled rejections directly.
// posthog-js reports a DOMException as type "DOMException" with the name prefixed to the value.
const isNoiseException = ({ type, value = "" }: { type?: string; value?: string }): boolean => {
  const lower = value.toLowerCase();

  if (type === "AbortError" || value.startsWith("AbortError")) return true;
  if (lower.includes("resizeobserver loop")) return true;
  if (lower.includes("script error")) return true;
  if (lower.includes("recaptcha")) return true;
  if (lower.includes("failed to fetch dynamically imported module")) return true;
  if (lower.includes("importing a module script failed")) return true;

  return false;
};

// Share links carry their credential as a path segment, which query-string masking doesn't reach.
const SHARE_TOKEN = /(\/shared\/[^/?#]+\/)[^/?#]+/g;
const URL_PROPERTIES = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$prev_pageview_pathname",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_pathname",
  "$session_entry_referrer",
];

const scrubCapture = (capture: CaptureResult | null): CaptureResult | null => {
  if (!capture) return capture;
  if (capture.event === "$exception") {
    const [exception] = (capture.properties.$exception_list ?? []) as { type?: string; value?: string }[];
    if (exception && isNoiseException(exception)) return null;
  }
  // posthog-js adds the document title to every pageview; on shared pages that is the chat or session name.
  if (capture.event === "$pageview") delete capture.properties.title;
  for (const properties of [capture.properties, capture.$set, capture.$set_once]) {
    if (!properties) continue;
    for (const key of URL_PROPERTIES) {
      const value: unknown = properties[key];
      if (typeof value === "string") properties[key] = value.replace(SHARE_TOKEN, "$1[token]");
    }
  }
  return capture;
};

export const posthogAnalyticsPlugin = (): AnalyticsPlugin => {
  const version = "v3";
  const source = `web:${version}`;

  // posthog-js is large and its init wires autocapture and replay, so both wait for the page to go
  // idle after boot. Every call chains on this promise, so calls made before then queue in order;
  // it stays undefined where PostHog never runs.
  let client: Promise<PostHog | undefined> | undefined;
  const loadPostHog = (): Promise<PostHog | undefined> | undefined => {
    if (client) return client;
    // @ts-ignore - import.meta.env is provided by Vite; typed in some but not all consumer tsconfigs
    if (import.meta.env.SSR || typeof POSTHOG_API_KEY === "undefined" || !POSTHOG_API_KEY) return undefined;
    client = new Promise<void>((resolve) => {
      if ("requestIdleCallback" in window) requestIdleCallback(() => resolve(), { timeout: 2000 });
      else setTimeout(resolve, 200);
    })
      .then(() => import("posthog-js"))
      .then(({ default: posthog }) => {
        const controlPlaneURL =
          (RUNNING_ENV === "local" && typeof STORYTELL_API_URL !== "undefined" && STORYTELL_API_URL) ||
          getControlPlaneURL(RUNNING_ENV);

        safely(() =>
          posthog.init(POSTHOG_API_KEY, {
            api_host: `${controlPlaneURL}/ingest`,
            // Unset, posthog-js derives the app host from api_host, which for a proxy
            // is the proxy itself and breaks toolbar and replay links.
            ui_host: "https://us.posthog.com",
            persistence: "localStorage",
            // The app sends one $pageview per path change itself. Page-leave defaults to following
            // capture_pageview, so it is kept on explicitly.
            capture_pageview: false,
            capture_pageleave: true,
            // Replay keeps text readable for usage review; inputs stay masked by default.
            // Transcripts are other meeting participants' speech, so their containers
            // carry ph-mask.
            session_recording: { maskTextSelector: ".ph-mask" },
            // Magic-link, unsubscribe and invite links carry credentials in the query string.
            mask_personal_data_properties: true,
            custom_personal_data_properties: ["magic_token", "token", "invite", "oobCode"],
            before_send: scrubCapture,
          })
        );

        safely(() => posthog.register({ source }));
        return posthog;
      })
      // A chunk that fails to load would otherwise surface as an unhandled rejection, which the
      // app reads as a stale deploy and reloads for.
      .catch(() => undefined);
    return client;
  };
  const withPostHog = (send: (posthog: PostHog) => void): void => {
    void loadPostHog()?.then((posthog) => posthog && safely(() => send(posthog)));
  };

  // No `loaded` hook: the analytics library would hold its own queue until it answered true, and
  // the client promise already orders every call behind init.
  const plugin: AnalyticsPlugin = {
    name: "posthog",
    initialize: () => {
      void loadPostHog();
    },
    identify: ({
      payload,
    }: {
      payload: {
        userId: string;
        traits: Properties;
      };
    }) => {
      withPostHog((posthog) => posthog.identify(payload.userId, { ...(payload.traits ?? {}), source }));
    },
    // Stamped at the call, since a capture made during boot reaches posthog-js only once it loads.
    track: (event: { payload: { event: string; properties: Properties } }) => {
      const timestamp = new Date();
      withPostHog((posthog) =>
        posthog.capture(event.payload.event, { ...event.payload.properties, source }, { timestamp })
      );
    },
    page: ({ payload }: { payload: { properties: { screen?: unknown } } }): void => {
      const timestamp = new Date();
      // The analytics library fills url, path and title from the browser; those carry magic-link
      // and share tokens and session names, so only the normalized screen is sent.
      withPostHog((posthog) => posthog.capture("$pageview", { screen: payload.properties.screen }, { timestamp }));
    },
    methods: {
      reset() {
        withPostHog((posthog) => posthog.reset());
      },
      setPersonProperties(properties: Properties) {
        withPostHog((posthog) => posthog.setPersonProperties(properties));
      },
      group(type: string, key: string) {
        withPostHog((posthog) => posthog.group(type, key));
      },
      register(properties: Properties) {
        withPostHog((posthog) => posthog.register(properties));
      },
      captureException(error: unknown, properties?: Properties) {
        withPostHog((posthog) => posthog.captureException(error, { ...properties, source }));
      },
    },
  };

  return plugin;
};
